import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import DramaCard from '@/components/feature/DramaCard';
import { getDramaById, dramas } from '@/mocks/dramas';

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = getDramaById(id || '');

  if (!drama) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查看的剧集不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedDramas = dramas
    .filter(d => d.id !== drama.id && (d.categorySlug === drama.categorySlug || d.genres.some(g => drama.genres.includes(g))))
    .slice(0, 6);

  const tvSeriesJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    name: drama.title,
    description: drama.description,
    numberOfEpisodes: drama.totalEpisodes,
    countryOfOrigin: drama.region,
    director: { '@type': 'Person', name: drama.director },
    actor: drama.cast.map(name => ({ '@type': 'Person', name })),
    genre: drama.genres,
    dateCreated: `${drama.year}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(tvSeriesJsonLd)}</script>

      <Navbar />

      <main className="pt-16 bg-background-50">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-6 border-b border-background-200/70">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: drama.category, href: `/category/${drama.categorySlug}` },
              { label: drama.title },
            ]}
          />
        </div>

        {/* Drama Info */}
        <section className="w-full px-4 md:px-6 py-8 md:py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Poster */}
            <div className="flex-shrink-0 w-full sm:w-64 lg:w-72">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={drama.posterImage}
                  alt={drama.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {drama.title}
              </h1>

              <div className="flex items-center flex-wrap gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  <i className="ri-star-fill text-xs"></i>
                  {drama.rating}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {drama.status}
                </span>
                <span className="text-xs text-foreground-500">{drama.updateInfo}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-5">
                {drama.genres.map((genre) => (
                  <span
                    key={genre}
                    className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs"
                  >
                    {genre}
                  </span>
                ))}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 w-16 flex-shrink-0">导演：</span>
                  <span className="text-sm text-foreground-800">{drama.director}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 w-16 flex-shrink-0">主演：</span>
                  <span className="text-sm text-foreground-800">{drama.cast.join(' / ')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 w-16 flex-shrink-0">地区：</span>
                  <span className="text-sm text-foreground-800">{drama.region}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 w-16 flex-shrink-0">年份：</span>
                  <span className="text-sm text-foreground-800">{drama.year}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-foreground-500 w-16 flex-shrink-0">集数：</span>
                  <span className="text-sm text-foreground-800">{drama.totalEpisodes}集</span>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{drama.description}</p>
              </div>

              <button
                onClick={() => {
                  const container = document.getElementById('episodes-section');
                  if (container) container.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </button>
            </div>
          </div>
        </section>

        {/* Episodes */}
        <section id="episodes-section" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
            分集列表
            <span className="text-sm font-normal text-foreground-500 ml-2">共{drama.totalEpisodes}集</span>
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2.5">
            {drama.episodes.map((ep) => (
              <button
                key={ep.number}
                className="px-3 py-2 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors text-center cursor-pointer whitespace-nowrap"
              >
                {ep.title}
              </button>
            ))}
          </div>
        </section>

        {/* Related Dramas */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-5">
            猜你喜欢
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {relatedDramas.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}